import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { AdminHeader, StatCard } from "../../components/admin/AdminUI";
import { Megaphone, Calendar, Image as ImageIcon, MessageSquare, Users, UserCog, ArrowRight, ClipboardList, HardDrive, Sparkles } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="text-brand-muted">Loading dashboard…</div>;
  const today = new Date();
  const chartData = stats.chart.labels.map((l, i) => ({
    name: l,
    announcements: stats.chart.announcements[i],
    events: stats.chart.events[i],
    messages: stats.chart.messages[i],
  }));

  return (
    <div className="space-y-8">
      <AdminHeader title="Dashboard" subtitle={today.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })} />

      {/* Welcome banner */}
      <div className="rounded-2xl bg-gradient-to-r from-brand-primary to-brand-secondary text-white p-6 md:p-8 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1 rounded-full text-xs uppercase tracking-widest"><Sparkles size={12} /> Overview</div>
          <h2 className="font-serif text-2xl md:text-3xl font-bold mt-3">Welcome to GOS Control Panel</h2>
          <p className="text-white/85 mt-2 text-sm">Manage members, announcements, events, gallery and website settings from a single place.</p>
        </div>
        <Link to="/" target="_blank" className="btn-primary bg-white text-brand-primary hover:bg-brand-primary-light" data-testid="dashboard-view-site">View Public Site <ArrowRight size={16} /></Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={Megaphone} label="Announcements" value={stats.totals.announcements} />
        <StatCard icon={Calendar} label="Events" value={stats.totals.events} color="secondary" />
        <StatCard icon={ImageIcon} label="Gallery Images" value={stats.totals.gallery_items} color="accent" />
        <StatCard icon={MessageSquare} label="Unread Messages" value={stats.totals.unread_messages} color="warn" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <StatCard icon={Users} label="Members" value={stats.totals.members} />
        <StatCard icon={UserCog} label="Committee" value={stats.totals.committee} color="secondary" />
        <StatCard icon={ClipboardList} label="Total Messages" value={stats.totals.messages} color="accent" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-elev p-6">
          <h3 className="font-serif text-lg font-bold text-brand-text mb-4">Activity Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip />
              <Line type="monotone" dataKey="announcements" stroke="#0056A6" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="events" stroke="#00AEEF" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="messages" stroke="#2ECC71" strokeWidth={2.5} dot={false} />
              <Legend />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card-elev p-6">
          <h3 className="font-serif text-lg font-bold text-brand-text mb-4">Content by Month</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip />
              <Bar dataKey="announcements" fill="#0056A6" radius={[8, 8, 0, 0]} />
              <Bar dataKey="events" fill="#00AEEF" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-elev p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg font-bold text-brand-text">Latest Messages</h3>
            <Link to="/admin/messages" className="text-sm text-brand-primary font-semibold">View all</Link>
          </div>
          <div className="space-y-3">
            {stats.latest_messages.map((m) => (
              <div key={m.id} className="p-3 rounded-lg border border-brand-border">
                <div className="flex items-center gap-2 justify-between">
                  <div className="font-semibold text-brand-text text-sm truncate">{m.name}</div>
                  {!m.is_read && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-accent text-white uppercase tracking-widest font-semibold">New</span>}
                </div>
                <div className="text-xs text-brand-muted truncate">{m.subject || m.message}</div>
              </div>
            ))}
            {stats.latest_messages.length === 0 && <div className="text-sm text-brand-muted">No messages yet.</div>}
          </div>
        </div>
        <div className="card-elev p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg font-bold text-brand-text">Latest Announcements</h3>
            <Link to="/admin/announcements" className="text-sm text-brand-primary font-semibold">View all</Link>
          </div>
          <div className="space-y-3">
            {stats.latest_announcements.map((a) => (
              <div key={a.id} className="p-3 rounded-lg border border-brand-border">
                <div className="font-semibold text-brand-text text-sm truncate">{a.title}</div>
                <div className="text-xs text-brand-muted mt-0.5">{a.category} · {new Date(a.publish_date).toLocaleDateString()}</div>
              </div>
            ))}
            {stats.latest_announcements.length === 0 && <div className="text-sm text-brand-muted">No announcements yet.</div>}
          </div>
        </div>
        <div className="card-elev p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg font-bold text-brand-text">Upcoming Events</h3>
            <Link to="/admin/events" className="text-sm text-brand-primary font-semibold">View all</Link>
          </div>
          <div className="space-y-3">
            {stats.upcoming_events.map((e) => {
              const d = new Date(e.event_date);
              return (
                <div key={e.id} className="p-3 rounded-lg border border-brand-border flex gap-3">
                  <div className="w-11 h-11 rounded-lg bg-brand-primary text-white flex flex-col items-center justify-center shrink-0">
                    <div className="text-[9px] uppercase">{d.toLocaleString("en", { month: "short" })}</div>
                    <div className="font-serif text-sm font-bold leading-none">{d.getDate()}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-brand-text text-sm truncate">{e.title}</div>
                    <div className="text-xs text-brand-muted truncate">{e.venue}</div>
                  </div>
                </div>
              );
            })}
            {stats.upcoming_events.length === 0 && <div className="text-sm text-brand-muted">No upcoming events.</div>}
          </div>
        </div>
      </div>

      {/* Storage widget */}
      <div className="card-elev p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-brand-primary-light text-brand-primary flex items-center justify-center"><HardDrive size={18} /></div>
          <div>
            <h3 className="font-serif text-lg font-bold text-brand-text">Storage Usage</h3>
            <p className="text-xs text-brand-muted">Media library capacity across gallery, downloads and images.</p>
          </div>
        </div>
        <div className="h-3 bg-brand-primary-light rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-brand-primary to-brand-secondary" style={{ width: "34%" }} />
        </div>
        <div className="flex justify-between text-xs text-brand-muted mt-2">
          <span>3.4 GB used</span><span>10 GB total</span>
        </div>
      </div>
    </div>
  );
}
